import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { createSocket, ISocket } from '@/lib/socketAdapter';
import { API_CONFIG } from '@/lib/api';

const chatLog = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.log(...args);
};

function getOtherUserId(roomId: string, myId: string): string | null {
  if (!roomId?.startsWith('dm_')) return null;
  const parts = roomId.split('_');
  if (parts.length !== 3) return null;
  return parts[1] === myId ? parts[2] : parts[1];
}

/** How long a room's loaded history is reused when the chat is reopened. */
const HISTORY_REUSE_MS = 2 * 60 * 1000;

type MessageStatus = 'sent' | 'delivered' | 'read';
const STATUS_RANK: Record<string, number> = { sent: 0, delivered: 1, read: 2 };

/**
 * The further of two statuses (sent -> delivered -> read). A status never
 * goes back: events and replies can arrive out of order, and a late
 * "delivered" used to replace a "read" already shown.
 */
function laterStatus(a?: string | null, b?: string | null): MessageStatus | undefined {
  const rank = (s?: string | null) => (s && s in STATUS_RANK ? STATUS_RANK[s] : -1);
  const best = rank(b) > rank(a) ? b : a;
  return (best ?? undefined) as MessageStatus | undefined;
}

interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  message_type: 'text' | 'image' | 'file' | 'system';
  metadata?: any;
  status?: 'sent' | 'delivered' | 'read';
  created_at: string;
  updated_at?: string;
  sender: {
    id: string;
    email: string;
    full_name: string;
    avatar_url?: string;
  } | null;
}

interface TeamMember {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: string;
  is_online: boolean;
  last_seen: string;
}

interface ChatContextType {
  isConnected: boolean;
  isConnecting: boolean;
  socket: ISocket | null;

  messages: ChatMessage[];
  teamMembers: TeamMember[];

  currentRoom: string | null;

  currentUser: any;

  typingUsers: { userId: string; roomId: string }[];

  unreadCounts: Record<string, number>;

  lastMessages: Record<string, ChatMessage>;

  connect: () => void;
  disconnect: () => void;
  joinRoom: (roomId: string) => void;
  joinConversationRoom: (conversationId: string) => void;
  leaveRoom: (roomId: string) => void;
  /** Resolves true once the message is saved. */
  sendMessage: (content: string, messageType?: string, metadata?: any) => Promise<boolean>;
  /** Resolves true once the message is saved. */
  sendDirectMessage: (recipientId: string, content: string, messageType?: string, metadata?: any) => Promise<boolean>;
  forwardMessage: (targetRoomId: string, content: string, messageType?: string, metadata?: any) => Promise<boolean>;
  editMessage: (messageId: string, newContent: string) => void;
  deleteMessage: (messageId: string) => void;
  clearChat: (roomId: string) => Promise<boolean>;
  setTyping: (isTyping: boolean, roomId?: string) => void;
  markAsRead: (memberId: string) => void;
  /** Load the DM inbox (previews + unread) once, for screens that show it. */
  ensureInboxLoaded: () => void;
  markDmMessagesAsRead: (roomId: string) => void;

  isLoadingMessages: boolean;
  isLoadingMembers: boolean;
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<ISocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [currentRoom, setCurrentRoom] = useState<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [typingUsers, setTypingUsers] = useState<{ userId: string; roomId: string }[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [lastMessages, setLastMessages] = useState<Record<string, ChatMessage>>({});
  // Bumped on every socket reconnect; events sent while the socket was down
  // are lost, so the inbox and presence are re-read once it is back.
  const [reconnectCount, setReconnectCount] = useState(0);
  const typingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // When each room's history was last fetched. Reopening a chat within
  // HISTORY_REUSE_MS reuses what is in memory (new messages keep arriving over
  // the socket) instead of re-reading it; edits/deletes are only pushed to the
  // room itself, so the reuse window is short. Cleared on every reconnect.
  const historyLoadedAtRef = useRef<Record<string, number>>({});
  const teamMembersLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The DM room this tab is subscribed to, left when another chat opens - with
  // the socket it was joined on: a new socket (after logging in again) has
  // joined nothing yet.
  // Statuses that arrived before their message (see messages_status_updated).
  const earlyStatusRef = useRef(new Map<string, string>());
  const withEarlyStatus = useCallback(<T extends { id?: string; status?: MessageStatus }>(message: T): T => {
    const early = message?.id ? earlyStatusRef.current.get(message.id) : undefined;
    if (!early) return message;
    earlyStatusRef.current.delete(message.id as string);
    return { ...message, status: laterStatus(message.status, early) };
  }, []);
  const joinedDmRoomRef = useRef<{ socket: ISocket; roomId: string } | null>(null);
  // The chat room last asked for by joinRoom - the one on screen. A slower
  // earlier joinRoom must not join, or become current, after it.
  const requestedRoomRef = useRef<string | null>(null);
  // Non-DM rooms joined through joinRoom on [socket], kept for its life.
  const keptRoomsRef = useRef<{ socket: ISocket | null; rooms: Set<string> }>({ socket: null, rooms: new Set() });

  const connect = useCallback(() => {
    if (socket || isConnecting) return;

    setIsConnecting(true);

    const envSocketUrl = import.meta.env.VITE_WEBSOCKET_URL;
    const socketUrl = envSocketUrl || API_CONFIG.BASE_URL.replace('/api/v1', '');

    const newSocket = createSocket(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      chatLog('[ChatContext] Socket.IO connected! ID:', newSocket.id);
      if (newSocket.id) void(0);
      setIsConnected(true);
      setIsConnecting(false);

      if (user?.id) {
        chatLog('[ChatContext] Authenticating with user ID:', user.id);
        newSocket.emit('authenticate', {
          userId: user.id,
          token: localStorage.getItem('auth_token') || undefined,
        });
      }
    });


    // This socket's own rooms are joined once: `authenticated` comes again on
    // every reconnect, and rooms are counted (lib/socketAdapter.ts), so each
    // repeat join would add one the socket never gives back. The adapter
    // rejoins them itself after a reconnect.
    let personalRoomsJoined = false;
    newSocket.on('authenticated', (data) => {
      chatLog('[ChatContext] Socket.IO authenticated:', data);
      if (personalRoomsJoined) return;
      personalRoomsJoined = true;

      if (user?.id) {
        chatLog('[ChatContext] Joining user room: user_' + user.id);
        newSocket.emit('join_room', { roomId: `user_${user.id}` });
      }

      if ((user as any)?.organization_id) {
        const orgRoom = `organization_${(user as any).organization_id}`;
        chatLog('[ChatContext] Joining organization room:', orgRoom);
        newSocket.emit('join_room', { roomId: orgRoom });
      }

    });

    // TEMPORARY DEBUG — remove once the publish-result delivery is understood.
    //
    // Logs EVERY notification the socket receives, at the socket itself, before
    // any feature handler runs. That is the one thing the existing logs cannot
    // tell us: `[SOCKET publish-now]` only prints from inside
    // SocialMediaPostsManager's handler, so an event that never reaches that
    // handler is invisible. This separates the two possibilities —
    //   line here + no [SOCKET publish-now] -> delivered, dropped by our code
    //   no line here at all                 -> never delivered to this browser
    // which decides whether the remaining bug is in the client or upstream.
    // NOTE: the socket here is `ISocket` (lib/socketAdapter.ts), which exposes
    // only on/off/emit — there is no `onAny`, so each event is subscribed by
    // name. These listeners only log; the feature handlers elsewhere still
    // receive the same events independently.
    const logRawSocketEvent = (event: string) => (payload: any) => {
      // eslint-disable-next-line no-console
      console.log('[RAW SOCKET]', event, {
        type: payload?.type,
        kind: payload?.data?.kind,
        platform: payload?.data?.platform || payload?.platform,
        scheduled_post_id: payload?.data?.scheduled_post_id || payload?.scheduled_post_id,
        notification_id: payload?.id,
        workspaceId: payload?.workspaceId ?? null,
        at: new Date().toISOString(),
      });
    };
    newSocket.on('notification', logRawSocketEvent('notification'));
    newSocket.on('scheduled_post_publishing', logRawSocketEvent('scheduled_post_publishing'));

    newSocket.on('disconnect', (reason) => {
      // Logged because a drop between publish and result would silently lose
      // whatever was emitted while disconnected — no retry exists.
      // eslint-disable-next-line no-console
      console.log('[RAW SOCKET] disconnected', { reason, at: new Date().toISOString() });
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      setIsConnecting(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setIsConnected(true);
      setReconnectCount(n => n + 1);

      if (user?.id) {
        newSocket.emit('authenticate', {
          userId: user.id,
          token: localStorage.getItem('auth_token') || undefined,
        });
      }
    });

    newSocket.on('reconnect_error', (error) => {
    });

    newSocket.on('reconnect_failed', () => {
      setIsConnected(false);
      setIsConnecting(false);
    });

    newSocket.on('message', (data) => {
      if (data.type === 'message' || data.type === 'direct_message') {
        setMessages(prev => {
          const existing = prev.find(m => m.id === data.data.id);
          if (existing) {
            const status = laterStatus(existing.status, data.data.status);
            return status === existing.status
              ? prev
              : prev.map(m => m.id === data.data.id ? { ...m, status } : m);
          }
          return [...prev, withEarlyStatus(data.data)];
        });

        if (data.type === 'direct_message' && data.data.room_id) {
          const otherId = getOtherUserId(data.data.room_id, user?.id || '');
          if (otherId) {
            setLastMessages(prev => ({ ...prev, [otherId]: data.data }));
            if (data.data.sender_id !== user?.id) {
              setUnreadCounts(prev => ({
                ...prev,
                [otherId]: (prev[otherId] || 0) + 1
              }));
            }
          }
        }

        if (data.type === 'message' && data.data.room_id && !data.data.room_id.startsWith('dm_')) {
          if (data.data.sender_id !== user?.id) {
            setUnreadCounts(prev => ({
              ...prev,
              [data.data.room_id]: (prev[data.data.room_id] || 0) + 1
            }));
          }
        }
      }
    });

    newSocket.on('chat_history', (data) => {
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.room_id !== data.roomId);
        return [...filtered, ...data.messages];
      });

      if (data.roomId?.startsWith('dm_') && data.messages.length > 0) {
        const otherId = getOtherUserId(data.roomId, user?.id || '');
        if (otherId) {
          setLastMessages(prev => ({
            ...prev,
            [otherId]: data.messages[data.messages.length - 1]
          }));
        }
      }
    });

    newSocket.on('message_updated', (data) => {
      setMessages(prev => prev.map(msg =>
        msg.id === data.messageId
          ? { ...msg, content: data.newContent, updated_at: data.updatedAt }
          : msg
      ));
    });

    newSocket.on('message_deleted', (data) => {
      setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
    });

    newSocket.on('messages_status_updated', (data) => {
      if (data.messageIds && data.status) {
        // A read can arrive before the message itself (it was sent and read
        // within milliseconds): kept, and applied when the message shows up.
        const early = earlyStatusRef.current;
        for (const id of data.messageIds) {
          const known = early.get(id);
          early.delete(id); // re-inserted below as the newest entry
          early.set(id, laterStatus(known, data.status) ?? data.status);
        }
        // Only the latest few matter (a message arrives within moments).
        while (early.size > 500) early.delete(early.keys().next().value as string);
        const idSet = new Set(data.messageIds);
        setMessages(prev => prev.map(msg =>
          idSet.has(msg.id) ? { ...msg, status: laterStatus(msg.status, data.status) } : msg
        ));
      }
    });


    newSocket.on('user_status', (data) => {
      setTeamMembers((prev) => {
        if (!prev || prev.length === 0) {
          return prev;
        }

        return prev.map((member) =>
          member.id === data.userId
            ? {
              ...member,
              is_online: data.is_online,
              last_seen: data.last_seen,
            }
            : member
        );
      });
    });

    // The socket is shared: notifications, the inbox and campaigns join their
    // own rooms (`business-account-…`, `user_…`, `organization_…`) on it, and
    // each join is confirmed with `room_joined`. Only a chat room (a DM or a
    // team room) is the chat's current room - adopting the others sent the
    // next team message into a room no chat shows.
    newSocket.on('room_joined', (data) => {
      const roomId = data?.roomId;
      // Only the room on screen: a late confirmation for a chat the user has
      // already left would send their next message there.
      if (typeof roomId === 'string' && roomId === requestedRoomRef.current
          && (roomId.startsWith('dm_') || /^org_.+_team_/.test(roomId))) {
        setCurrentRoom(roomId);
      }
    });

    // The room on screen is never cleared by the confirmation of leaving an
    // older one (this handler was registered with a stale currentRoom).
    newSocket.on('room_left', (data) => {
      if (data?.roomId && data.roomId !== requestedRoomRef.current) {
        setCurrentRoom(prev => (prev === data.roomId ? null : prev));
      }
    });

    newSocket.on('user_typing', (data) => {
      const key = `${data.userId}_${data.roomId}`;

      if (typingTimeoutsRef.current[key]) {
        clearTimeout(typingTimeoutsRef.current[key]);
        delete typingTimeoutsRef.current[key];
      }

      if (data.isTyping) {
        setTypingUsers(prev => {
          if (prev.some(t => t.userId === data.userId && t.roomId === data.roomId)) return prev;
          return [...prev, { userId: data.userId, roomId: data.roomId }];
        });
        typingTimeoutsRef.current[key] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(t => !(t.userId === data.userId && t.roomId === data.roomId)));
          delete typingTimeoutsRef.current[key];
        }, 3000);
      } else {
        setTypingUsers(prev => prev.filter(t => !(t.userId === data.userId && t.roomId === data.roomId)));
      }
    });

    newSocket.on('error', (data) => {
    });

    newSocket.on('conversation_updated', (data) => {
    });

    newSocket.on('conversations_updated', (data) => {
    });

    newSocket.on('messages_marked_read', (data) => {
    });

    setSocket(newSocket);
  }, [socket, isConnecting, user?.id, currentRoom]);

  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  }, [socket]);

  const joinRoom = useCallback(async (roomId: string) => {
    chatLog('[ChatContext] Joining room:', roomId);
    setCurrentRoom(roomId);
    requestedRoomRef.current = roomId;

    // DM rooms: the one this socket holds is in joinedDmRoomRef, set when its
    // join is sent and cleared when it is left. Opening another chat leaves
    // it - rooms used to pile up, one per DM opened, for the life of the tab
    // (a subscription row each on AWS). Nothing is missed: a DM's messages
    // also reach the user's own room. The team room is kept: its unread
    // badge needs it. Not connected: nothing is sent now - joinRoom runs
    // again once connected (it changes with isConnected).
    const heldDm = () => (socket && joinedDmRoomRef.current?.socket === socket
      ? joinedDmRoomRef.current.roomId
      : null);
    if (socket && isConnected) {
      const previousDm = heldDm();
      if (previousDm && previousDm !== roomId) {
        socket.emit('leave_room', { roomId: previousDm });
        joinedDmRoomRef.current = null;
      }
    }

    const loadedAt = historyLoadedAtRef.current[roomId];
    if (!loadedAt || Date.now() - loadedAt > HISTORY_REUSE_MS) {
      try {
        const { ChatService } = await import('@/lib/api/services/chatService');
        const response = await ChatService.getChatHistory(roomId, 50, 0);

        if (response.success && Array.isArray(response.data)) {
          chatLog('[ChatContext] Chat history loaded:', response.data.length, 'messages');
          historyLoadedAtRef.current[roomId] = Date.now();

          setMessages(prev => {
            const filtered = prev.filter(msg => msg.room_id !== roomId);
            return [...filtered, ...response.data];
          });
        }
      } catch (error) {
        console.error('[ChatContext] Error loading chat history:', error);
      }
    }

    // Another chat was opened while this history loaded: joining now would
    // hold a room no chat shows (and its `room_joined` used to make it the
    // current room). The newer call does the joining.
    if (requestedRoomRef.current !== roomId) return;
    if (!socket || !isConnected) return;

    // Checked now, not before the load: a call for the same room may have
    // joined it meanwhile. Rooms are counted per join and left once, so a
    // room is joined once - the team room once per socket.
    if (roomId.startsWith('dm_')) {
      if (heldDm() !== roomId) {
        const stale = heldDm();
        if (stale) socket.emit('leave_room', { roomId: stale });
        socket.emit('join_room', { roomId });
        joinedDmRoomRef.current = { socket, roomId };
      }
    } else {
      const held = keptRoomsRef.current;
      if (held.socket !== socket) {
        held.socket = socket;
        held.rooms = new Set();
      }
      if (!held.rooms.has(roomId)) {
        held.rooms.add(roomId);
        socket.emit('join_room', { roomId });
      }
    }
  }, [socket, isConnected]);

  const joinConversationRoom = useCallback((conversationId: string) => {
    if (socket && isConnected) {
      const roomId = `conversation_${conversationId}`;
      chatLog('[ChatContext] Joining room:', roomId);
      socket.emit('join_room', { roomId });
    } else {
      console.warn('[ChatContext] Cannot join room - socket:', !!socket, 'isConnected:', isConnected);
    }
  }, [socket, isConnected]);

  const leaveRoom = useCallback((roomId: string) => {
    if (socket && isConnected) {
      if (keptRoomsRef.current.socket === socket) keptRoomsRef.current.rooms.delete(roomId);
      if (joinedDmRoomRef.current?.socket === socket && joinedDmRoomRef.current.roomId === roomId) {
        joinedDmRoomRef.current = null;
      }
      socket.emit('leave_room', {
        roomId
      });
    }
  }, [socket, isConnected]);

  const sendMessage = useCallback(async (content: string, messageType = 'text', metadata?: any) => {
    if (!currentRoom) {
      console.error('[ChatContext] Cannot send message - no room selected');
      return false;
    }

    if (!user?.id) {
      console.error('[ChatContext] Cannot send message - user not authenticated');
      return false;
    }

    try {
      chatLog('[ChatContext] Sending message via REST API to room:', currentRoom);
      
      const { ChatService } = await import('@/lib/api/services/chatService');
      
      const response = await ChatService.sendMessage({
        room_id: currentRoom,
        content,
        message_type: messageType as any,
        metadata
      });

      if (response.success && response.data) {
        chatLog('[ChatContext] Message sent successfully:', response.data);
        
        const messageData = response.data;
        setMessages(prev => {
          if (prev.some(m => m.id === messageData.id)) return prev;
          return [...prev, messageData];
        });
        return true;
      } else {
        console.error('[ChatContext] Failed to send message:', response);
        return false;
      }
    } catch (error) {
      console.error('[ChatContext] Error sending message:', error);
      return false;
    }
  }, [currentRoom, user?.id]);

  const sendDirectMessage = useCallback(async (recipientId: string, content: string, messageType = 'text', metadata?: any) => {
    if (!user?.id) {
      console.error('[ChatContext] Cannot send message - user not authenticated');
      return false;
    }

    try {
      chatLog('[ChatContext] Sending direct message via REST API to:', recipientId);
      
      const { ChatService } = await import('@/lib/api/services/chatService');
      
      const response = await ChatService.sendDirectMessage(user.id, {
        recipientId,
        content,
        message_type: messageType as any,
        metadata
      });

      if (response.success && response.data) {
        chatLog('[ChatContext] Direct message sent successfully:', response.data);

        const messageData = response.data as any;
        setMessages(prev => {
          const existing = prev.find(m => m.id === messageData.id);
          if (existing) {
            // The live "read" may already be here; the reply can be older.
            return prev.map(m => m.id === messageData.id
              ? { ...m, ...messageData, status: laterStatus(m.status, messageData.status) }
              : m);
          }
          return [...prev, withEarlyStatus(messageData)];
        });

        setLastMessages(prev => ({
          ...prev,
          [recipientId]: messageData
        }));
        return true;
      } else {
        console.error('[ChatContext] Failed to send direct message:', response);
        return false;
      }
    } catch (error) {
      console.error('[ChatContext] Error sending direct message:', error);
      return false;
    }
  }, [user?.id]);

  const forwardMessage = useCallback(async (
    targetRoomId: string,
    content: string,
    messageType = 'text',
    metadata?: any
  ): Promise<boolean> => {
    if (!user?.id) {
      console.error('[ChatContext] Cannot forward message - user not authenticated');
      return false;
    }
    if (!targetRoomId) {
      console.error('[ChatContext] Cannot forward message - targetRoomId missing');
      return false;
    }
    try {
      const { ChatService } = await import('@/lib/api/services/chatService');
      const response = await ChatService.sendMessage({
        room_id: targetRoomId,
        content,
        message_type: messageType as any,
        metadata
      });
      if (response.success && response.data) {
        const messageData = response.data as any;
        setMessages(prev => {
          if (prev.some(m => m.id === messageData.id)) return prev;
          return [...prev, messageData];
        });
        if (targetRoomId.startsWith('dm_')) {
          const otherId = getOtherUserId(targetRoomId, user.id);
          if (otherId) {
            setLastMessages(prev => ({ ...prev, [otherId]: messageData }));
          }
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('[ChatContext] Error forwarding message:', err);
      return false;
    }
  }, [user?.id]);

  const setTyping = useCallback((isTyping: boolean, roomId?: string) => {
    const targetRoom = roomId || currentRoom;
    if (socket && isConnected && targetRoom) {
      socket.emit('typing', {
        roomId: targetRoom,
        isTyping
      });
    }
  }, [socket, isConnected, currentRoom]);

  const markAsRead = useCallback((memberId: string) => {
    setUnreadCounts(prev => {
      if (prev[memberId]) {
        const { [memberId]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const editMessage = useCallback((messageId: string, newContent: string) => {
    if (socket && isConnected) {
      socket.emit('edit_message', {
        messageId,
        newContent
      });
    }
  }, [socket, isConnected]);

  const deleteMessage = useCallback((messageId: string) => {
    if (socket && isConnected) {
      socket.emit('delete_message', {
        messageId
      });
    }
  }, [socket, isConnected]);

  const markDmMessagesAsRead = useCallback((roomId: string) => {
    if (socket && isConnected && roomId) {
      socket.emit('mark_messages_read', { roomId });
    }
  }, [socket, isConnected]);

  const clearChat = useCallback(async (roomId: string): Promise<boolean> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return false;

      const res = await fetch(`${API_CONFIG.BASE_URL}/chat/clear/${encodeURIComponent(roomId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) return false;

      setMessages(prev => prev.filter(msg => msg.room_id !== roomId));

      if (roomId.startsWith('dm_')) {
        const parts = roomId.split('_');
        const otherUserId = parts[1] === user?.id ? parts[2] : parts[1];
        if (otherUserId) {
          setLastMessages(prev => {
            const { [otherUserId]: _, ...rest } = prev;
            return rest;
          });
        }
      }

      return true;
    } catch (error) {
      console.error('[ChatContext] Error clearing chat:', error);
      return false;
    }
  }, [user?.id]);

  const loadTeamMembers = useCallback(async () => {
    chatLog('[ChatContext] Loading team members via REST API...');
    setIsLoadingMembers(true);
    
    try {
      const { TeamService } = await import('@/lib/api/services/teamService');
      
      const response = await TeamService.getTeamMembersAllPages();
      
      if (response.success && Array.isArray(response.data)) {
        chatLog('[ChatContext] Team members loaded successfully:', response.data.length);
        
        const members = response.data.map((member: any) => ({
          id: member.id,
          email: member.email,
          full_name: member.name || member.full_name || member.email,
          avatar_url: member.avatar_url || null,
          role: member.role || 'USER',
          is_online: member.is_online || false,
          last_seen: member.last_seen || new Date().toISOString()
        }));
        
        setTeamMembers(members);
      } else {
        console.warn('[ChatContext] Failed to load team members:', response);
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('[ChatContext] Error loading team members:', error);
      setTeamMembers([]);
    } finally {
      setIsLoadingMembers(false);
      
      if (teamMembersLoadTimeoutRef.current) {
        clearTimeout(teamMembersLoadTimeoutRef.current);
        teamMembersLoadTimeoutRef.current = null;
      }
    }
  }, []);

  /*
   * DM sidebar state (last message + unread per teammate) from the server, so
   * it survives a reload instead of starting empty. One request; live
   * `direct_message` events keep it current afterwards.
   */
  const loadInbox = useCallback(async (myId: string) => {
    try {
      const { ChatService } = await import('@/lib/api/services/chatService');
      const response = await ChatService.getInbox();
      if (!response.success || !Array.isArray(response.data)) return;

      const unread: Record<string, number> = {};
      const last: Record<string, ChatMessage> = {};
      for (const entry of response.data) {
        const otherId = getOtherUserId(entry.room_id, myId);
        if (!otherId) continue;
        unread[otherId] = entry.unread || 0;
        if (entry.last_message) last[otherId] = entry.last_message as ChatMessage;
      }

      setUnreadCounts(prev => {
        const next = { ...prev, ...unread };
        for (const key of Object.keys(next)) if (!next[key]) delete next[key];
        return next;
      });
      setLastMessages(prev => {
        const next = { ...prev };
        for (const [otherId, msg] of Object.entries(last)) {
          const current = next[otherId];
          if (!current || String(current.created_at) < String(msg.created_at)) next[otherId] = msg;
        }
        return next;
      });
    } catch (error) {
      console.error('[ChatContext] Error loading chat inbox:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      chatLog('[ChatContext] Auto-loading team members via REST API...');
      loadTeamMembers();
    }
  }, [user?.id, loadTeamMembers]);

  /*
   * The DM inbox is only shown by the team messaging screens, but this
   * provider wraps the whole app — loading it here cost a request (plus the
   * auth checks behind it) on every page load for every user. Screens that
   * show it call ensureInboxLoaded(); once requested, it is also re-read after
   * a reconnect. Live `direct_message` events keep it current meanwhile.
   */
  const inboxRequestedForRef = useRef<string | null>(null);
  const ensureInboxLoaded = useCallback(() => {
    if (!user?.id || inboxRequestedForRef.current === user.id) return;
    inboxRequestedForRef.current = user.id;
    loadInbox(user.id);
  }, [user?.id, loadInbox]);

  useEffect(() => {
    if (!reconnectCount || !user?.id) return;
    // Anything sent while the socket was down is missing from memory.
    historyLoadedAtRef.current = {};
    loadTeamMembers();
    if (inboxRequestedForRef.current === user.id) loadInbox(user.id);
  }, [reconnectCount, user?.id, loadTeamMembers, loadInbox]);

  /*
   * A socket belongs to the user it was opened for. Its handlers close over
   * that user, and on the socket.io transport it stays joined to that user's
   * `user_<id>` room — so if the signed-in user changes without a page load
   * (session expiry → sign-in as someone else, account switch), the next user
   * kept receiving the previous user's notifications and messages. Tear it
   * down on any identity change; the effect below reconnects as the new user.
   */
  const socketOwnerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!socket) {
      socketOwnerRef.current = user?.id ?? null;
      return;
    }
    if (socketOwnerRef.current !== (user?.id ?? null)) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setIsConnecting(false);
    }
  }, [user?.id, socket]);

  useEffect(() => {
    if (user?.id && !socket && !isConnecting) {
      socketOwnerRef.current = user.id;
      connect();
    }
  }, [user?.id, socket, isConnecting, connect]);


  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      if (teamMembersLoadTimeoutRef.current) {
        clearTimeout(teamMembersLoadTimeoutRef.current);
      }
    };
  }, [socket]);

  const value: ChatContextType = {
    isConnected,
    isConnecting,
    socket,
    messages,
    teamMembers,
    currentRoom,
    currentUser: user,
    typingUsers,
    unreadCounts,
    lastMessages,
    connect,
    disconnect,
    joinRoom,
    joinConversationRoom,
    leaveRoom,
    sendMessage,
    sendDirectMessage,
    forwardMessage,
    editMessage,
    deleteMessage,
    clearChat,
    setTyping,
    markAsRead,
    ensureInboxLoaded,
    markDmMessagesAsRead,
    isLoadingMessages,
    isLoadingMembers
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  return context;
}
